import {
    Component,
    forwardRef,
    inject,
    OnInit,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DatepickerIntervalComponent } from "@components/datepicker-interval/datepicker-interval.component";
import { HeaderComponent } from "@components/header/header.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { orderBy } from "lodash";
import moment from "moment";
import { BehaviorSubject } from "rxjs";
import { Avverkningsgrupper } from "~app/common/models/constants";
import { DateUtils } from "~app/common/utils/date-utils";
import { IMaskinLagBase } from "~app/features/planera/model/IMaskinLagBase";
import { ArbetsorderSearchService } from "~app/features/planera/services/arbetsorder-search.service";
import { ArbetsorderSearchFilterComponent } from "../../components/arbetsorder-search-filter/arbetsorder-search-filter.component";
import { ArbetsorderSearchBasePanel } from "../../models/arbetsorder-search-base-panel";

@Component({
    selector: "rask-arbetsorder-search-grot",
    templateUrl: "./arbetsorder-search-grot.component.html",
    styleUrls: ["./arbetsorder-search-grot.component.scss"],
    providers: [
        { provide: ArbetsorderSearchBasePanel,
            useExisting: forwardRef(() => ArbetsorderSearchGrotComponent) }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ArbetsorderSearchFilterComponent,
        RadiobuttonGroupComponent,
        FormsModule,
        MultiSelectorComponent,
        HeaderComponent,
        DatepickerIntervalComponent
    ]
})
export class ArbetsorderSearchGrotComponent extends ArbetsorderSearchBasePanel implements OnInit {
    allaMaskinlag: BehaviorSubject<IMaskinLagBase[]> = new BehaviorSubject([]);

    private readonly arbetsorderSokService = inject(ArbetsorderSearchService);

    areDatePickersDisabled(): boolean {
        return !(this.searchFilter.grot.skordad);
    }

    changeGrotskotningValue(value: any) {
        const index = this.searchFilter.avverkningsgrupper.indexOf(Avverkningsgrupper.Grotskotning);
        if (value === true && index === -1) {
            this.searchFilter.avverkningsgrupper.push(Avverkningsgrupper.Grotskotning);
        } else if (value === false && index > -1) {
            this.searchFilter.avverkningsgrupper.splice(index, 1);
        }
    }

    forceUpdateFilterChange(): void {
        // Metoden är abstrakt så den måste finnas även om den är tom
    }

    getEndDate() {
        if (this.searchFilter.grot.skordadTo) {
            return moment(this.searchFilter.grot.skordadTo).set({
                hour: 0,
                minute: 0,
                second: 0
            }).format();
        } else {
            return null;
        }
    }

    getGrotskordadValue() {
        if (this.searchFilter.grot.skordad === true) {
            return 1;
        } else if (this.searchFilter.grot.skordad === false) {
            return 0;
        } else {
            return -1;
        }
    }

    getGrotskotningMedRundvirkeValue() {
        return(this.searchFilter.grot.grotRelateratRundvirkeProduktionGodkand === true);
    }

    getGrotskotningValue(): number {
        if (this.isGrotskotningActive()) {
            if (this.searchFilter.grot.grotRelateratRundvirkeProduktionGodkand === true) {
                return 2;
            }
            return 1;
        }
        return 0;
    }

    getStartDate() {
        if (this.searchFilter.grot.skordadFrom) {
            return moment(this.searchFilter.grot.skordadFrom).set({
                hour: 0,
                minute: 0,
                second: 0
            }).format();
        } else {
            return null;
        }
    }

    isDirty(): boolean {
        return (
            (typeof this.searchFilter.grot.skordad !== "undefined") ||
            (this.searchFilter.grot.grotRelateratRundvirkeMaskinlag?.length > 0) ||
            (this.isGrotskotningActive())
        );
    }

    isGrotskotningActive() {
        return (this.searchFilter.avverkningsgrupper.indexOf(Avverkningsgrupper.Grotskotning) > -1);
    }

    ngOnInit(): void {
        this.setupMaskinlag();
    }

    onChangeMaskinlag(maskinlag: IMaskinLagBase[]) {
        this.searchFilter.grot.grotRelateratRundvirkeMaskinlag = maskinlag;
    }

    tradslagViewFormatterFn = (item: IMaskinLagBase): string => item ? `${item.csrNummer} ${item.namn}` : "";

    searchMaskinlagFn = (val: string, item: IMaskinLagBase) => this.tradslagViewFormatterFn(item).toUpperCase().includes(val?.toUpperCase()) ?? false;

    setEndDate(date: any) {
        if (date) {
            this.searchFilter.grot.skordadTo =  DateUtils.endOfDayDateTime(date);
        } else {
            this.searchFilter.grot.skordadTo = null;
        }
    }

    setGrotskordadValue(value: any) {
        if (value === 1) {
            this.searchFilter.grot.skordad = true;
        } else if (value === 0) {
            this.searchFilter.grot.skordad = false;
        } else {
            delete this.searchFilter.grot.skordad;
        }
    }

    setGrotskotningValue(value: any) {
        if (value === 1) {
            this.searchFilter.grot.grotRelateratRundvirkeProduktionGodkand = false;
            this.changeGrotskotningValue(true);
        } else if (value === 2) {
            this.searchFilter.grot.grotRelateratRundvirkeProduktionGodkand = true;
            this.changeGrotskotningValue(true);
        } else {
            this.searchFilter.grot.grotRelateratRundvirkeProduktionGodkand = false;
            this.changeGrotskotningValue(false);
        }
    }

    setStartDate(date: any) {
        if (date) {
            this.searchFilter.grot.skordadFrom = DateUtils.startOfDayDateTime(date);
        } else {
            this.searchFilter.grot.skordadFrom = null;
        }
    }

    setupMaskinlag() {
        this.arbetsorderSokService.getAllMaskinlag().subscribe({
            next: (allaMaskinlag: IMaskinLagBase[]) => {
                const orderedMaskinlag = orderBy(allaMaskinlag, m => m.csrNummer);
                this.allaMaskinlag.next(orderedMaskinlag);
            }
        });
    }
}
